import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import PageSection from '../../../../features/components/PageSection'
import SkeletonListRow from '../../../../features/components/List/Skeleton'
import List from '../../../../features/components/List'
import ActionsDropdown from '../../../../features/components/ActionsDropdown'
import DeleteItemModal from '../../../../features/components/Modals/DeleteItemModal'

import useDeleteAction from '../../../../features/hooks/useDeleteAction'

import { insertIf } from '../../../../helpers/common'

import { openForm } from '../../../../modules/actions/forms'
import { clearDeletePartnership, deletePartnership } from '../../../../modules/actions/partnerships'
import {
  partnershipsErrorSelector,
  partnershipsIsLoadingSelector,
  partnershipsSelector,
  partnershipsWasLoadedSelector,
  deletePartnershipSelector,
  deletePartnershipIsLoadingSelector,
  deletePartnershipErrorSelector,
  updatePartnershipIsLoadingSelector,
  updatePartnershipSelector,
  selectedPartnershipSelector
} from '../../../../modules/selectors/partnerships'

import { PARTNERSHIP_EDIT } from '../../../../constants/forms'

import useCommonStyles from '../../../../styles/common/listPage'
import useStyles from './styles'

const PartnershipsList = () => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()

  const dispatch = useDispatch()

  const partnerships = useSelector(partnershipsSelector)

  const { id: deletedPartnershipId } = useSelector(deletePartnershipSelector)
  const deletePartnershipIsLoading = useSelector(deletePartnershipIsLoadingSelector)

  const { id: updatePartnershipId } = useSelector(updatePartnershipSelector)
  const updatePartnershipIsLoading = useSelector(updatePartnershipIsLoadingSelector)

  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { rights_holder: selectedPartnershipRightsHolderId } = selectedPartnership

  const filteredPartnerships = useMemo(
    () => partnerships.filter(item => item.rights_holder === selectedPartnershipRightsHolderId),
    [partnerships, selectedPartnershipRightsHolderId]
  )

  const isMoreThanOnePartnership = filteredPartnerships.length > 1

  const openPartnershipEditForm = useCallback(
    id => {
      dispatch(openForm({ id, formName: PARTNERSHIP_EDIT }))
    },
    [dispatch]
  )

  const deletePartnershipHandler = useCallback(
    params => {
      dispatch(deletePartnership(params))
    },
    [dispatch]
  )

  const clearDeletePartnershipHandler = useCallback(() => {
    dispatch(clearDeletePartnership())
  }, [dispatch])

  const { handleDelete, handleDeleteRequest, handleCloseDeleteModal, isDeleteModalOpened, deleteItemIsLoading } =
    useDeleteAction({
      onDelete: deletePartnershipHandler,
      onClearDelete: clearDeletePartnershipHandler,
      deletedItemSelector: deletePartnershipSelector,
      deleteItemErrorSelector: deletePartnershipErrorSelector,
      deleteItemIsLoadingSelector: deletePartnershipIsLoadingSelector
    })

  const getDropdownOptions = useCallback(
    partnership => [
      {
        text: 'Edit',
        onClickHandler: () => openPartnershipEditForm(partnership.id)
      },
      ...insertIf(isMoreThanOnePartnership, {
        text: 'Delete',
        onClickHandler: () => handleDeleteRequest({ item: partnership })
      })
    ],
    [openPartnershipEditForm, handleDeleteRequest, isMoreThanOnePartnership]
  )

  const itemsListColumns = useMemo(
    () => [
      {
        Component: ({ img, icon }) => (
          <img className={classes.icon} src={img || icon} alt="icon" onError={i => (i.target.style.display = 'none')} />
        ),
        style: {
          maxWidth: 65,
          maxHeight: 50,
          textOverflow: 'unset'
        }
      },
      {
        headTitle: 'Name',
        field: 'name',
        style: {
          minWidth: 200,
          textOverflow: 'unset'
        }
      },
      {
        Component: partnership => <ActionsDropdown options={getDropdownOptions(partnership)} />,
        style: {
          maxWidth: 30
        }
      }
    ],
    [classes.icon, getDropdownOptions]
  )

  return (
    <>
      <ItemsLoading
        className={commonClasses.itemList}
        itemsLength={partnerships.length}
        SkeletonComponent={SkeletonListRow}
        errorSelector={partnershipsErrorSelector}
        isLoadingSelector={partnershipsIsLoadingSelector}
        wasLoadedSelector={partnershipsWasLoadedSelector}
        ContentWrapper={PageSection}
        isEmptyNoDataMessage
      >
        <List
          cols={itemsListColumns}
          listData={filteredPartnerships}
          itemDeletingId={deletePartnershipIsLoading ? deletedPartnershipId : ''}
          itemUpdatingId={updatePartnershipIsLoading ? updatePartnershipId : ''}
        />
      </ItemsLoading>
      <DeleteItemModal
        handleDelete={handleDelete}
        handleCloseDeleteModal={handleCloseDeleteModal}
        isDeleteModalOpened={isDeleteModalOpened}
        deleteItemIsLoading={deleteItemIsLoading}
        deleteModalMessage="This can't be undone"
        deleteButtonText="Yes, Delete"
      />
    </>
  )
}

export default PartnershipsList
